import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';

const ALGO_8410_WIDTH = 800;
const ALGO_8410_HEIGHT = 480;
const MAX_IMAGE_BYTES = 2500000;

const FIT_MODES = {
  letterbox: 'Letterbox (fit with black bars)',
  crop: 'Crop to Fill (may cut edges)',
  stretch: 'Stretch (may distort)',
};

const ImageDisplayModal = ({ open, onClose, onSubmit, actionLabel, loading = false }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [processedPreview, setProcessedPreview] = useState(null);
  const [overlayText, setOverlayText] = useState('');
  const [fitMode, setFitMode] = useState('letterbox');
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setProcessedPreview(null);
    setOverlayText('');
    setFitMode('letterbox');
    setError(null);
    setProcessing(false);
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file (PNG, JPEG, etc.)');
      return;
    }

    setError(null);
    setSelectedFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (!previewUrl) {
      setProcessedPreview(null);
      return;
    }

    setProcessing(true);
    const img = new Image();
    img.onload = () => {
      processImage(img);
      setProcessing(false);
    };
    img.onerror = () => {
      setError('Failed to load image');
      setProcessing(false);
    };
    img.src = previewUrl;
  }, [previewUrl, fitMode]);

  const processImage = (img) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = ALGO_8410_WIDTH;
    canvas.height = ALGO_8410_HEIGHT;

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, ALGO_8410_WIDTH, ALGO_8410_HEIGHT);

    const srcWidth = img.width;
    const srcHeight = img.height;
    const srcAspect = srcWidth / srcHeight;
    const dstAspect = ALGO_8410_WIDTH / ALGO_8410_HEIGHT;

    let drawX = 0, drawY = 0, drawWidth = ALGO_8410_WIDTH, drawHeight = ALGO_8410_HEIGHT;
    let srcX = 0, srcY = 0, srcDrawWidth = srcWidth, srcDrawHeight = srcHeight;

    if (fitMode === 'letterbox') {
      if (srcAspect > dstAspect) {
        drawWidth = ALGO_8410_WIDTH;
        drawHeight = ALGO_8410_WIDTH / srcAspect;
        drawY = (ALGO_8410_HEIGHT - drawHeight) / 2;
      } else {
        drawHeight = ALGO_8410_HEIGHT;
        drawWidth = ALGO_8410_HEIGHT * srcAspect;
        drawX = (ALGO_8410_WIDTH - drawWidth) / 2;
      }
      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    } else if (fitMode === 'crop') {
      if (srcAspect > dstAspect) {
        srcDrawWidth = srcHeight * dstAspect;
        srcX = (srcWidth - srcDrawWidth) / 2;
      } else {
        srcDrawHeight = srcWidth / dstAspect;
        srcY = (srcHeight - srcDrawHeight) / 2;
      }
      ctx.drawImage(img, srcX, srcY, srcDrawWidth, srcDrawHeight, 0, 0, ALGO_8410_WIDTH, ALGO_8410_HEIGHT);
    } else if (fitMode === 'stretch') {
      ctx.drawImage(img, 0, 0, ALGO_8410_WIDTH, ALGO_8410_HEIGHT);
    }

    setProcessedPreview(canvas.toDataURL('image/png'));
  };

  const handleSubmit = () => {
    if (!processedPreview) {
      setError('Please select an image first');
      return;
    }

    try {
      const raw = String(processedPreview || '').trim().replace(/^data:[^;]+;base64,/i, '');
      const approxBytes = Math.floor((raw.length * 3) / 4);
      if (approxBytes > MAX_IMAGE_BYTES) {
        setError(`Image is too large (${approxBytes} bytes). Please choose a smaller image.`);
        return;
      }
    } catch (e) {
      setError('Failed to validate image size');
      return;
    }

    onSubmit({
      imageBase64: processedPreview,
      filename: selectedFile?.name || 'display_image.png',
      overlayText: overlayText.trim(),
      fitMode,
    });
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {actionLabel ? `Send Image: ${actionLabel}` : 'Send Image to Display'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
            <Button variant="outlined" onClick={handleBrowseClick} fullWidth>
              {selectedFile ? `Selected: ${selectedFile.name}` : 'Browse for Image...'}
            </Button>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              Supported formats: PNG, JPEG, GIF, WebP
            </Typography>
          </Box>

          <FormControl fullWidth>
            <InputLabel>Fit Mode</InputLabel>
            <Select
              value={fitMode}
              label="Fit Mode"
              onChange={(e) => setFitMode(e.target.value)}
            >
              {Object.entries(FIT_MODES).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Overlay Text (optional)"
            value={overlayText}
            onChange={(e) => setOverlayText(e.target.value)}
            placeholder="e.g., LOCKDOWN IN EFFECT"
            fullWidth
            helperText="Text will be displayed over the image on the device"
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Preview (800×480)
            </Typography>
            <Box
              sx={{
                width: '100%',
                maxWidth: 400,
                aspectRatio: '800/480',
                backgroundColor: '#000',
                border: '1px solid #ccc',
                borderRadius: 1,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
              }}
            >
              {processing ? (
                <CircularProgress size={40} />
              ) : processedPreview ? (
                <img
                  src={processedPreview}
                  alt="Preview"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              ) : (
                <Typography color="grey.500">No image selected</Typography>
              )}
            </Box>
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </Box>

          {overlayText && (
            <Alert severity="info" icon={false}>
              <Typography variant="body2">
                <strong>Overlay Text:</strong> "{overlayText}"
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!processedPreview || loading || processing}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Sending...' : 'Send to Display'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageDisplayModal;
